import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { adminDeleteSocialPost, adminListSocialPosts, adminUpsertSocialPost } from "@/lib/social-admin.functions";
import { adminSocialDeliveryStatus } from "@/lib/social-delivery.functions";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { MediaLightbox, SocialPreview, socialMediaUrls } from "@/components/social/SocialPreview";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  List as ListIcon,
  RefreshCw,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { etDateTimeLocalToDate } from "@/lib/et-datetime";

type AccountDelivery = {
  account_id: string;
  name: string;
  platform: string;
  status: "published" | "scheduled" | "failed" | "pending" | "unknown";
  error: string | null;
  permalink: string | null;
};
type PostDelivery = {
  post_id: string;
  external_post_id: string | null;
  overall: "published" | "scheduled" | "partial" | "failed" | "not_sent" | "unknown";
  accounts: AccountDelivery[];
  checked_at: string;
};

const DELIVERY_LABEL: Record<PostDelivery["overall"], string> = {
  published: "Confirmed published",
  scheduled: "Confirmed scheduled",
  partial: "Partly delivered",
  failed: "Delivery failed",
  not_sent: "Not sent to GHL",
  unknown: "Unconfirmed",
};

function deliveryTone(overall: PostDelivery["overall"]): string {
  switch (overall) {
    case "published":
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
    case "scheduled":
      return "bg-primary/15 text-primary";
    case "partial":
      return "bg-amber-500/20 text-amber-700 dark:text-amber-400";
    case "failed":
      return "bg-destructive/15 text-destructive";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function DeliveryIcon({ status }: { status: AccountDelivery["status"] }) {
  if (status === "published") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
  if (status === "failed") return <XCircle className="h-3.5 w-3.5 text-destructive" />;
  if (status === "scheduled" || status === "pending") return <Clock className="h-3.5 w-3.5 text-primary" />;
  return <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" />;
}

function DeliveryBadge({ delivery }: { delivery?: PostDelivery }) {
  if (!delivery) return null;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${deliveryTone(delivery.overall)}`}>
      {DELIVERY_LABEL[delivery.overall]}
    </span>
  );
}

function DeliveryDetail({ delivery }: { delivery?: PostDelivery }) {
  if (!delivery) return null;
  if (delivery.accounts.length === 0) {
    return <p className="text-xs text-muted-foreground">No connected account recorded for this post.</p>;
  }
  return (
    <ul className="space-y-1">
      {delivery.accounts.map((account) => (
        <li key={account.account_id} className="flex items-start gap-2 text-xs">
          <DeliveryIcon status={account.status} />
          <span className="font-medium">{account.name}</span>
          <span className="uppercase text-muted-foreground">{account.platform}</span>
          <span className="text-muted-foreground">— {account.status}</span>
          {account.error && <span className="text-destructive">{account.error}</span>}
          {account.permalink && (
            <a href={account.permalink} target="_blank" rel="noreferrer" className="text-primary underline">
              View
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

export const Route = createFileRoute("/admin/social/calendar")({
  head: () => ({ meta: [
    { title: "Social Calendar · Simply Approved Admin" },
    { name: "description", content: "Review scheduled social campaigns and delivery states." },
    { property: "og:title", content: "Social Calendar · Simply Approved Admin" },
    { property: "og:description", content: "Review scheduled social campaigns and delivery states." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex, nofollow" },
  ] }),
  component: CalendarPage,
});

type Post = {
  id: string;
  body: string;
  platforms: string[];
  status: string;
  scheduled_at: string | null;
  published_at: string | null;
  link_url: string | null;
  media_urls: string[] | null;
  error_message: string | null;
  created_at: string;
  external_ids?: Record<string, any> | null;
  tags?: string[] | null;
  source_blog_post_id?: string | null;
};

const ET = "America/New_York";
export function socialCalendarDayKey(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: ET, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
}
function formatEt(date: Date, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { ...options, timeZone: ET }).format(date);
}

function postDate(post: Post): Date | null {
  const value = post.scheduled_at ?? post.published_at ?? (post.status === "draft" ? post.created_at : null);
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function statusBadge(status: string): string {
  const s = status.toLowerCase();
  if (s.includes("publish")) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
  if (s.includes("schedul")) return "bg-primary/15 text-primary";
  if (s.includes("draft")) return "bg-accent text-accent-foreground";
  if (s.includes("fail") || s.includes("error")) return "bg-destructive/15 text-destructive";
  return "bg-muted text-muted-foreground";
}

type PostedState = { label: string; icon: string; tone: string };
/** Plain answer to "did this actually post?" for every calendar entry. */
export function postedState(post: Post, delivery?: PostDelivery, now = Date.now()): PostedState {
  const when = postDate(post);
  const due = when ? when.getTime() <= now : false;
  const ok = "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
  const bad = "bg-destructive/15 text-destructive";
  const warn = "bg-amber-500/20 text-amber-700 dark:text-amber-400";
  if (delivery?.overall === "published" || (post.status === "published" && !delivery)) return { label: "Posted", icon: "✓", tone: ok };
  if (delivery?.overall === "partial") return { label: "Partly posted", icon: "⚠", tone: warn };
  if (delivery?.overall === "failed" || post.status === "failed") return { label: "Failed — not posted", icon: "✕", tone: bad };
  if (post.status === "draft") return due ? { label: "Not posted (draft)", icon: "✕", tone: bad } : { label: "Draft", icon: "✎", tone: "bg-accent text-accent-foreground" };
  if (due) {
    if (delivery?.overall === "not_sent") return { label: "Not posted", icon: "✕", tone: bad };
    return { label: "Not confirmed posted", icon: "?", tone: warn };
  }
  return { label: "Scheduled", icon: "⏱", tone: "bg-primary/15 text-primary" };
}

function PostedBadge({ state }: { state: PostedState }) {
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${state.tone}`}>{state.icon} {state.label}</span>;
}

function CalendarPage() {
  const list = useServerFn(adminListSocialPosts);
  const del = useServerFn(adminDeleteSocialPost);
  const save = useServerFn(adminUpsertSocialPost);
  const deliveryFn = useServerFn(adminSocialDeliveryStatus);
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["social-posts"], queryFn: () => list() });
  const deliveryQuery = useQuery({
    queryKey: ["social-delivery"],
    queryFn: () => deliveryFn({ data: {} }),
    refetchInterval: 5 * 60 * 1000,
  });

  const [view, setView] = useState<"calendar" | "week" | "list">("calendar");
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [platformFilter, setPlatformFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [preview, setPreview] = useState<Post | null>(null);
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);
  const [rescheduleAt, setRescheduleAt] = useState("");

  const deliveryData = deliveryQuery.data as unknown as
    | { connected: boolean; error: string | null; posts: PostDelivery[] }
    | undefined;
  const deliveryById = useMemo(() => {
    const map = new Map<string, PostDelivery>();
    for (const d of deliveryData?.posts ?? []) map.set(d.post_id, d);
    return map;
  }, [deliveryData]);

  const allPosts = (query.data ?? []) as unknown as Post[];
  const platforms = useMemo(() => {
    const set = new Set<string>();
    allPosts.forEach((post) => (post.platforms ?? []).forEach((platform) => set.add(platform)));
    return Array.from(set).sort();
  }, [allPosts]);

  const posts = useMemo(() => {
    return allPosts.filter((post) => {
      if (statusFilter !== "all" && !post.status.toLowerCase().includes(statusFilter)) return false;
      if (platformFilter !== "all" && !(post.platforms ?? []).includes(platformFilter)) return false;
      const source = String(post.external_ids?.source ?? "other");
      if (sourceFilter === "blog" && !post.source_blog_post_id && !(post.tags ?? []).includes("blog")) return false;
      if (sourceFilter === "regular" && source !== "weekly_regular_social") return false;
      if (sourceFilter === "other" && (post.source_blog_post_id || (post.tags ?? []).includes("blog") || source === "weekly_regular_social")) return false;
      if (issuesOnly) {
        const d = deliveryById.get(post.id);
        const bad = d && (d.overall === "failed" || d.overall === "partial");
        if (!bad && !post.error_message) return false;
      }
      return true;
    });
  }, [allPosts, platformFilter, statusFilter, sourceFilter, issuesOnly, deliveryById]);

  const issueCount = useMemo(
    () => (deliveryData?.posts ?? []).filter((d) => d.overall === "failed" || d.overall === "partial").length,
    [deliveryData],
  );

  const days = useMemo(() => {
    const gridStart = startOfWeek(view === "week" ? cursor : startOfMonth(cursor), { weekStartsOn: 0 });
    const gridEnd = view === "week" ? endOfWeek(cursor, { weekStartsOn: 0 }) : endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [cursor, view]);

  const postsByDay = useMemo(() => {
    const map = new Map<string, Post[]>();
    for (const post of posts) {
      const date = postDate(post);
      if (!date) continue;
      const key = socialCalendarDayKey(date);
      const rows = map.get(key) ?? [];
      rows.push(post);
      rows.sort((a, b) => (postDate(a)?.getTime() ?? 0) - (postDate(b)?.getTime() ?? 0));
      map.set(key, rows);
    }
    return map;
  }, [posts]);

  const remove = async (id: string) => {
    if (!confirm("Delete this social post?")) return;
    try {
      await del({ data: { id } });
      toast.success("Deleted");
      setPreview(null);
      await queryClient.invalidateQueries({ queryKey: ["social-posts"] });
    } catch (error: any) {
      toast.error(error?.message ?? "Delete failed");
    }
  };

  const reschedule = async (post: Post) => {
    if (!rescheduleAt) return toast.error("Choose a future Eastern Time date and time.");
    const at = etDateTimeLocalToDate(rescheduleAt) ?? new Date(NaN);
    if (Number.isNaN(at.getTime()) || at.getTime() <= Date.now()) return toast.error("Choose a future date and time.");
    try {
      await save({ data: { id: post.id, platforms: post.platforms, body: post.body, link_url: post.link_url, media_urls: post.media_urls, external_ids: post.external_ids, status: "draft", scheduled_at: at.toISOString() } });
      toast.success("Recommended publish time updated. The post remains a draft.");
      setPreview(null);
      setRescheduleAt("");
      await queryClient.invalidateQueries({ queryKey: ["social-posts"] });
    } catch (error: any) { toast.error(error?.message ?? "Could not update the time"); }
  };

  return (
    <div>
      <SocialSubNav />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-primary">Planner &amp; Calendar</h1>
          <p className="text-sm text-muted-foreground">Drafts, scheduled posts, published posts, images, and GHL status in one place.</p>
        </div>
        <div className="flex rounded-lg border border-border p-0.5">
          <Button type="button" size="sm" variant={view === "calendar" ? "default" : "ghost"} onClick={() => setView("calendar")}>
            <CalendarDays className="mr-1.5 h-4 w-4" /> Month
          </Button>
          <Button type="button" size="sm" variant={view === "week" ? "default" : "ghost"} onClick={() => setView("week")}>
            <CalendarDays className="mr-1.5 h-4 w-4" /> Week
          </Button>
          <Button type="button" size="sm" variant={view === "list" ? "default" : "ghost"} onClick={() => setView("list")}>
            <ListIcon className="mr-1.5 h-4 w-4" /> List
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select value={platformFilter} onChange={(event) => setPlatformFilter(event.target.value)} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm">
          <option value="all">All platforms</option>
          {platforms.map((platform) => <option key={platform} value={platform}>{platform}</option>)}
        </select>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm">
          <option value="all">All statuses</option>
          <option value="draft">Drafts</option>
          <option value="schedul">Scheduled</option>
          <option value="publish">Published</option>
          <option value="fail">Errors</option>
        </select>
        <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm">
          <option value="all">All sources</option><option value="blog">Blog-derived</option><option value="regular">Weekly regular</option><option value="other">Other</option>
        </select>
        <Button type="button" size="sm" variant={issuesOnly ? "default" : "outline"} onClick={() => setIssuesOnly((v) => !v)}>
          <AlertTriangle className="mr-1.5 h-4 w-4" /> Delivery issues{issueCount > 0 ? ` (${issueCount})` : ""}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={deliveryQuery.isFetching}
          onClick={() => void deliveryQuery.refetch()}
        >
          <RefreshCw className={`mr-1.5 h-4 w-4 ${deliveryQuery.isFetching ? "animate-spin" : ""}`} /> Re-check delivery
        </Button>
      </div>

      {deliveryData && !deliveryData.connected && (
        <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300">
          Delivery can't be confirmed with the social accounts right now{deliveryData.error ? `: ${deliveryData.error}` : "."} Statuses below reflect the last known local state.
        </div>
      )}

      {query.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}


      {(view === "calendar" || view === "week") && (
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="mb-3 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setCursor((current) => view === "week" ? addWeeks(current, -1) : addMonths(current, -1))}><ChevronLeft className="h-4 w-4" /></Button>
            <div className="flex items-center gap-3">
              <span className="font-display text-lg font-semibold text-primary">{view === "week" ? `Week of ${format(startOfWeek(cursor, { weekStartsOn: 0 }), "MMM d, yyyy")}` : format(cursor, "MMMM yyyy")}</span>
              <Button variant="outline" size="sm" onClick={() => setCursor(startOfMonth(new Date()))}>Today</Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setCursor((current) => view === "week" ? addWeeks(current, 1) : addMonths(current, 1))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-px text-center text-[11px] font-semibold uppercase text-muted-foreground">
            {[
              "Sun",
              "Mon",
              "Tue",
              "Wed",
              "Thu",
              "Fri",
              "Sat",
            ].map((day) => <div key={day} className="pb-1">{day}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md bg-border">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayPosts = postsByDay.get(key) ?? [];
              return (
                <div key={key} className={`min-h-[112px] bg-card p-1.5 ${isSameMonth(day, cursor) ? "" : "opacity-40"}`}>
                  <div className={`mb-1 text-right text-xs ${isSameDay(day, new Date()) ? "font-bold text-primary" : "text-muted-foreground"}`}>{format(day, "d")}</div>
                  <div className="space-y-1">
                    {dayPosts.slice(0, 4).map((post) => {
                      const media = socialMediaUrls(post.media_urls);
                      const delivery = deliveryById.get(post.id);
                      const state = postedState(post, delivery);
                      return (
                        <button
                          key={post.id}
                          type="button"
                          title={`${state.label} — ${post.body}`}
                          onClick={() => setPreview(post)}
                          className={`block w-full rounded px-1 py-0.5 text-left text-[10px] ${state.tone}`}
                        >
                          <span className="block font-bold">{state.icon} {state.label}</span>
                          <span className="block truncate">{media.length > 0 ? "▣ " : ""}{postDate(post) ? formatEt(postDate(post) as Date, { hour: "numeric", minute: "2-digit" }) + " " : ""}{post.body.slice(0, 24)}</span>
                        </button>
                      );
                    })}
                    {dayPosts.length > 4 && <div className="text-[10px] text-muted-foreground">+{dayPosts.length - 4} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="space-y-2">
          {posts.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No posts match these filters.</p>
          ) : posts.slice().sort((a, b) => (postDate(a)?.getTime() ?? 0) - (postDate(b)?.getTime() ?? 0)).map((post) => (
            <div key={post.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className={`rounded-full px-2 py-0.5 font-semibold uppercase ${statusBadge(post.status)}`}>{post.status}</span>
                    <PostedBadge state={postedState(post, deliveryById.get(post.id))} /><DeliveryBadge delivery={deliveryById.get(post.id)} />
                    {(post.platforms ?? []).map((platform) => <span key={platform} className="rounded-full bg-accent px-2 py-0.5 font-semibold uppercase text-accent-foreground">{platform}</span>)}
                     {postDate(post) && <span className="text-muted-foreground">{formatEt(postDate(post) as Date, { dateStyle: "medium", timeStyle: "short" })} ET</span>}
                     {post.external_ids?.prepared_at && <span className="text-muted-foreground">Prepared {formatEt(new Date(post.external_ids.prepared_at), { dateStyle: "medium" })}</span>}
                    {(post.media_urls ?? []).length > 0 && <span className="text-muted-foreground">{(post.media_urls ?? []).length} media</span>}
                  </div>
                  <button type="button" onClick={() => setPreview(post)} className="mt-2 block w-full text-left">
                    <p className="line-clamp-3 whitespace-pre-wrap text-sm hover:text-primary">{post.body || <span className="italic text-muted-foreground">No caption</span>}</p>
                  </button>
                  <div className="mt-2"><DeliveryDetail delivery={deliveryById.get(post.id)} /></div>
                  {post.error_message && (
                    <p className="mt-1 text-xs text-destructive">{post.error_message}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setPreview(post)}><Eye className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(post.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${statusBadge(preview.status)}`}>{preview.status}</span>
                  <PostedBadge state={postedState(preview, deliveryById.get(preview.id))} /><DeliveryBadge delivery={deliveryById.get(preview.id)} />
                  {(preview.platforms ?? []).map((platform) => <span key={platform} className="rounded-full bg-accent px-2 py-0.5 text-xs font-semibold uppercase text-accent-foreground">{platform}</span>)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {postDate(preview) && <p className="text-xs text-muted-foreground">Recommended publish: {formatEt(postDate(preview) as Date, { dateStyle: "full", timeStyle: "short" })} ET</p>}
                {preview.external_ids?.prepared_at && <p className="text-xs text-muted-foreground">Prepared: {formatEt(new Date(preview.external_ids.prepared_at), { dateStyle: "full", timeStyle: "short" })} ET</p>}
                <div className="rounded-md border border-border p-3">
                  <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Delivery by account</p>
                  <DeliveryDetail delivery={deliveryById.get(preview.id)} />
                </div>
                <SocialPreview body={preview.body} mediaUrls={socialMediaUrls(preview.media_urls)} onOpenMedia={(index) => setLightbox({ images: socialMediaUrls(preview.media_urls), index })} />
                <div className="flex flex-col gap-2 sm:flex-row"><input type="datetime-local" aria-label="New recommended publish time, Eastern Time" value={rescheduleAt} onChange={(event) => setRescheduleAt(event.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm" /><Button type="button" variant="outline" onClick={() => void reschedule(preview)}>Update draft time</Button></div>
                {preview.error_message && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">{preview.error_message}</div>}
                <div className="flex justify-end"><Button variant="ghost" size="sm" onClick={() => remove(preview.id)}><Trash2 className="mr-1 h-4 w-4" /> Delete</Button></div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {lightbox && <MediaLightbox images={lightbox.images} index={lightbox.index} onClose={() => setLightbox(null)} onIndex={(index) => setLightbox((state) => (state ? { ...state, index } : state))} />}
    </div>
  );
}